import type { SurveyPage } from '../../fetchSurvey/types';
import { logScrollWarning } from '../logger/navigationLogger';
import { toSdkLoggableError } from '../../types/shared';

/**
 * Resolves the DOM element corresponding to a given question ID.
 * Expects the developer to assign standard ID attributes matching the question identifier.
 *
 * @param questionId The unique identifier of the target question.
 * @returns The resolved HTMLElement, or null if server-side rendering or element not found.
 */
export function findQuestionElement(questionId: string): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  return document.getElementById(questionId) || null;
}

/**
 * Resolves the logical survey container boundary wrapping the question.
 * Looks for semantic HTML containers (main, form) or defaults to parent hierarchy.
 *
 * @param firstQuestionEl The element of the first question on the active page.
 * @returns The resolved survey container element.
 */
export function findSurveyContainer(firstQuestionEl: HTMLElement): HTMLElement {
  return (
    firstQuestionEl.closest('main') ||
    firstQuestionEl.closest('form') ||
    firstQuestionEl.parentElement ||
    firstQuestionEl
  );
}

/**
 * Scrolls the viewport to the top of the survey container for the specified page index.
 *
 * @param pages List of survey pages.
 * @param pageIndex The target page index to navigate and scroll to.
 */
export function scrollToFirstQuestionOfPage(pages: SurveyPage[], pageIndex: number): void {
  try {
    const targetPage = pages[pageIndex];
    const firstQuestion = targetPage?.questions?.[0];
    if (firstQuestion) {
      setTimeout(() => {
        try {
          const questionEl = findQuestionElement(firstQuestion.id);
          if (questionEl) {
            const surveyContainer = findSurveyContainer(questionEl);
            surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        } catch (err) {
          logScrollWarning('Failed to scroll page first question into view', toSdkLoggableError(err));
        }
      }, 50);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  } catch (err) {
    logScrollWarning('Failed to trigger scroll navigation', toSdkLoggableError(err));
  }
}
